import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from '@angular/core';
import {
  AuthService,
  ProfileService,
  SelectedTeamService,
  TeamInvitationsCrudService,
  TeamsCrudService,
} from '@tokenbox/services';
import { PaymentService } from '../../../../shared/payment/services/payment.service';
import { environment } from '../../../../../environments/environment.development';
import { Avatar } from 'primeng/avatar';
import { Menu } from 'primeng/menu';
import { MenuItem, PrimeIcons } from 'primeng/api';
import { RouterLink } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { Badge, BadgeDirective } from 'primeng/badge';
import { AcceptTeamInvitationsService } from '../../../../shared/accept-team-invations/services/accept-team-invitations.service';
import { InAppSupportService } from '../../../../shared/services/support/in-app-support.service.ts';

@Component({
  selector: 'dt-sidebar-menu',
  imports: [AsyncPipe, Avatar, Menu, RouterLink, Badge, BadgeDirective],
  templateUrl: './sidebar-menu.component.html',
  styleUrl: './sidebar-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarMenuComponent {
  protected readonly PrimeIcons = PrimeIcons;
  public readonly authService = inject(AuthService);
  public readonly profileService = inject(ProfileService);
  public readonly paymentService = inject(PaymentService);
  private teamsCrudService = inject(TeamsCrudService);
  private selectedTeamService = inject(SelectedTeamService);
  private teamInvitationsCrudService = inject(TeamInvitationsCrudService);
  private acceptTeamInvitationsService = inject(AcceptTeamInvitationsService);
  private inAppSupportService = inject(InAppSupportService);

  public readonly contactEmail: string = environment.contact.email;

  selectedTeam = this.selectedTeamService.selectedTeam;
  pendingInvitations = toSignal(
    this.teamInvitationsCrudService.pendingInvitationsForCurrentUser$,
    { initialValue: [] },
  );

  showUpgradeButton = computed(() => {
    const plan = this.selectedTeam()?.plan;
    const canManageTeam = this.selectedTeam()?.ownPermissions?.MANAGE_TEAM;
    return plan && !plan.paidPlan && canManageTeam;
  });

  upgradeUrl = computed(() => {
    const teams = this.teamsCrudService.allTeams();
    if (teams[0]?.id) {
      return this.paymentService.buildCheckoutUrlForCurrentUser(teams[0].id);
    }
    return `/admin/teams`;
  });

  async logout() {
    await this.authService.logout();
    document.location.href = window.origin;
  }

  items = computed<MenuItem[]>(() => {
    const items: MenuItem[] = [
      {
        separator: true,
      },
    ];

    if (this.showUpgradeButton()) {
      items.push({
        label: 'Upgrade to Pro plan',
        icon: PrimeIcons.STAR,
        url: this.upgradeUrl(),
      });
    } else if (this.selectedTeam()?.ownPermissions?.MANAGE_TEAM) {
      items.push({
        label: 'Manage Subscription',
        icon: PrimeIcons.CROWN,
        url: this.paymentService.customerPortalUrl,
      });
    }

    const pendingInvitations = this.pendingInvitations();
    items.push(
      {
        label: 'Pending Invitations',
        icon: PrimeIcons.USER_PLUS,
        badge: `${pendingInvitations.length}`,
        disabled: pendingInvitations.length < 1,
        command: () =>
          this.acceptTeamInvitationsService.showPendingInvitationsDialog(),
      },
      {
        label: 'Profile',
        icon: PrimeIcons.USER,
        routerLink: '/admin/profile',
      },
      {
        label: 'Teams',
        icon: PrimeIcons.USERS,
        routerLink: '/admin/teams',
      },
      {
        label: 'Support',
        icon: PrimeIcons.COMMENT,
        command: () => this.inAppSupportService.showChat(),
      },
      {
        label: 'Logout',
        icon: PrimeIcons.SIGN_OUT,
        command: () => this.logout(),
      },
    );

    return items;
  });
}
